import React, { Fragment, useState, useRef } from 'react';
import { Row, Col, Card, CardBody, CardHeader, FormGroup, Label, Modal, ModalHeader, ModalBody, ModalFooter, Badge } from 'reactstrap';
import { H5, H6, Btn, Spinner } from '../../../../AbstractElements';
import axios from 'axios';
import config from '../../../../config';
import * as XLSX from 'xlsx';
import DataTable from 'react-data-table-component';
import Widgets1 from '../../../Common/CommonWidgets/Widgets1';
import { toast } from 'react-toastify';

const TEMPLATE_COLUMNS = ['tenant_code', 'store_name', 'property', 'month', 'net_sale', 'transaction_count'];
const NUMBER_COLUMNS = ['net_sale', 'transaction_count'];

const formatDateTime = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleString();
};

const STATUS_COLOR = { Skipped: 'warning', Failed: 'danger' };

// Only the rows that did not import - the ones that did need no explanation.
const buildErrorRows = (item) => (item && item.failed_rows ? item.failed_rows : []).map(r => ({
    status: r.status || 'Failed',
    tenant_code: r.tenant_code,
    store_name: r.store_name,
    month: r.month,
    reason: r.reason
}));

const errorColumns = [
    {
        name: 'Status',
        selector: row => row.status,
        cell: row => (
            <Badge color={STATUS_COLOR[row.status] || 'secondary'}>{row.status}</Badge>
        ),
        sortable: true,
        width: '110px'
    },
    {
        name: 'Tenant Code',
        selector: row => row.tenant_code || '-',
        sortable: true
    },
    {
        name: 'Store Name',
        selector: row => row.store_name || '-',
        sortable: true
    },
    {
        name: 'Month',
        selector: row => row.month || '-',
        sortable: true
    },
    {
        name: 'Reason',
        selector: row => row.reason || '-',
        cell: row => (
            <span className="txt-danger fw-bold">{row.reason}</span>
        ),
        wrap: true,
        grow: 2
    }
];

const allImportedMessage = (count) =>
    `All ${count} row${count === 1 ? '' : 's'} were imported successfully.`;

// Expanding a history row reveals only the rows that did not import.
const HistoryDetail = ({ data }) => {
    const rows = buildErrorRows(data);
    return (
        <div className="p-3">
            {rows.length > 0 ? (
                <DataTable
                    data={rows}
                    columns={errorColumns}
                    striped
                    dense
                    pagination
                    paginationPerPage={5}
                />
            ) : (
                <p className="mb-0 txt-success">{allImportedMessage(data.imported || 0)}</p>
            )}
        </div>
    );
};

const BulkImportView = () => {

    if (localStorage.getItem("bulkDataUpload") !== "1") {
        alert('You are not authorized to view this page');
        window.history.back(); // Navigate back to the previous URL
    }

    const fileRef = useRef();

    const token = localStorage.getItem("sessiontoken");
    const configheader = {
        headers: {
            Authorization: `${token}`
        }
    };
    const concessionaire_id = localStorage.getItem("concessionaireId");
    const login_id = localStorage.getItem("loginId");
    const login_name = localStorage.getItem("loginName");

    const [file, setFile] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [resultModal, setResultModal] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [historyModal, setHistoryModal] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [history, setHistory] = useState([]);

    const handleFileChange = (f) => {
        if (!f) return;
        if (!f.name.match(/\.(xlsx|xls|csv)$/i)) {
            setError('Only .xlsx, .xls and .csv files are supported.');
            return;
        }
        setError('');
        setFile(f);
    };

    const handleTemplateDownload = () => {
        const worksheet = XLSX.utils.aoa_to_sheet([TEMPLATE_COLUMNS]);
        worksheet['!cols'] = [{ wch: 16 }, { wch: 28 }, { wch: 24 }, { wch: 12 }, { wch: 14 }, { wch: 20 }];

        const instructions = XLSX.utils.aoa_to_sheet([
            ['Bulk Sales Import - Instructions'],
            [],
            ['1.', 'Do not rename, reorder or remove the columns on the "Sales" sheet.'],
            ['2.', 'tenant_code', 'The tenant code as registered under your concessionaire. This is what the data is matched on.'],
            ['3.', 'store_name', 'Optional check. If filled it must match the store name registered against the tenant code.'],
            ['4.', 'property', 'Optional check. If filled it must match the property registered against the tenant code.'],
            ['5.', 'month', 'Format YYYY-MM, for example 2026-07. Enter it as text so Excel does not convert it to a date.'],
            ['6.', 'net_sale', 'Total net sales for the whole month. Numbers only, no currency symbols or thousand separators.'],
            ['7.', 'transaction_count', 'Total number of transactions for the whole month. Whole numbers only.'],
            [],
            ['Rules'],
            ['-', 'One row per tenant per month. Repeating the same tenant and month in one file rejects both rows.'],
            ['-', 'The month must be fully in the past. The current month and future months are not accepted.'],
            ['-', 'The monthly totals are spread evenly across every day of the month when they are saved.'],
            ['-', 'If sales data already exists for a tenant in that month, that row is skipped and reported. Nothing is overwritten.'],
            ['-', 'Rows that fail are listed in the upload summary. Fix them and upload again - rows that already imported will simply be skipped.']
        ]);
        instructions['!cols'] = [{ wch: 5 }, { wch: 20 }, { wch: 95 }];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sales');
        XLSX.utils.book_append_sheet(workbook, instructions, 'Instructions');
        XLSX.writeFile(workbook, 'bulk_sales_import_template.xlsx');
    };

    const insertToLog = (task) => {
        const data = {
            created_by: login_id,
            concessionaire_id: concessionaire_id,
            module: "Admin",
            task: `${login_name} ${task}`
        }
        axios
            .post(`${config.hostname}/log`, data, configheader)
            .then(() => {
                console.log("inserted to log table")
            })
            .catch((error) => {
                console.log(error);
            });
    }

    // Excel often turns a "2026-07" cell into a real date, so accept both shapes.
    const normalizeMonth = (value) => {
        if (value instanceof Date && !isNaN(value.getTime())) {
            return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}`;
        }
        const match = String(value == null ? '' : value).trim().match(/^(\d{4})[-/](\d{1,2})$/);
        if (!match) return null;
        const monthNumber = Number(match[2]);
        if (monthNumber < 1 || monthNumber > 12) return null;
        return `${match[1]}-${String(monthNumber).padStart(2, '0')}`;
    };

    const handleUpload = async () => {
        if (!file) {
            setError('Please select a file before uploading');
            return;
        }

        setError('');

        let rows;
        try {
            const workbook = XLSX.read(await file.arrayBuffer(), { cellDates: true });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        } catch {
            setError('Failed to read the file. Please make sure it is a valid Excel or CSV file.');
            return;
        }

        if (!rows || rows.length === 0) {
            setError('Uploaded file is empty.');
            return;
        }

        const headers = Object.keys(rows[0] || {});
        const missingColumns = TEMPLATE_COLUMNS.filter(col => !headers.includes(col));
        if (missingColumns.length > 0) {
            setError('Invalid file structure. Please use the provided template.');
            return;
        }

        const hasBlank = rows.some(row =>
            TEMPLATE_COLUMNS.some(col =>
                col !== 'store_name' && col !== 'property' &&
                (row[col] === undefined || row[col] === null || String(row[col]).trim() === '')
            )
        );
        if (hasBlank) {
            setError('Uploaded file contains blank values in tenant code, month, net sale or transaction count.');
            return;
        }

        const invalidNumber = rows.some(row => NUMBER_COLUMNS.some(col => isNaN(Number(row[col]))));
        if (invalidNumber) {
            setError('Net sale and transaction count must contain valid numbers only.');
            return;
        }

        const badMonthRow = rows.find(row => normalizeMonth(row.month) === null);
        if (badMonthRow) {
            setError(`Month must be in YYYY-MM format (e.g. 2026-07). Check tenant code "${badMonthRow.tenant_code}".`);
            return;
        }

        const formattedData = rows.map(r => ({
            tenant_code: String(r.tenant_code).trim(),
            store_name: String(r.store_name == null ? '' : r.store_name).trim(),
            property: String(r.property == null ? '' : r.property).trim(),
            month: normalizeMonth(r.month),
            net_sale: Number(r.net_sale),
            transaction_count: Number(r.transaction_count)
        }));

        setLoading(true);
        axios.post(
            `${config.hostname}/salesTransactions/bulkimport`,
            {
                concessionaire_id: concessionaire_id,
                login_id: login_id,
                file_name: file.name,
                rows: formattedData
            },
            {
                headers: {
                    Authorization: token,
                    'Content-Type': 'application/json'
                }
            }
        )
            .then(res => {
                const summary = res.data.upload_summary || {};
                setUploadResult(res.data);
                setResultModal(true);

                if ((summary.failed || 0) + (summary.skipped || 0) > 0) {
                    toast.warning('Bulk import completed with some rows not imported');
                } else {
                    toast.success('Bulk import completed successfully');
                }

                if (summary.imported > 0) {
                    insertToLog(`bulk imported sales for ${summary.imported} tenant(s)`);
                }
            })
            .catch(err => {
                const message = err.response?.data?.message || 'Upload failed.';
                setError(message);
                toast.error(message);
            })
            .finally(() => {
                setLoading(false);
                clearFile(); // keep any error message visible
            });
    };

    const handleShowHistory = () => {
        setHistoryModal(true);
        setHistoryLoading(true);
        axios.post(
            `${config.hostname}/salesTransactions/bulkimporthistory`,
            { concessionaire_id: concessionaire_id, limit: 50 },
            configheader
        )
            .then(res => setHistory(res.data || []))
            .catch(err => {
                console.log(err);
                setHistory([]);
                toast.error('Could not load upload history');
            })
            .finally(() => setHistoryLoading(false));
    };

    const clearFile = () => {
        setFile(null);
        if (fileRef.current) fileRef.current.value = '';
    };

    const handleReset = () => {
        clearFile();
        setError('');
    };

    const historyColumns = [
        {
            name: 'Uploaded On',
            selector: row => row.performed_at || '',
            format: row => formatDateTime(row.performed_at),
            sortable: true,
            width: '190px'
        },
        {
            name: 'File',
            selector: row => row.file_name || '-',
            sortable: true,
            wrap: true
        },
        {
            name: 'Uploaded By',
            selector: row => row.uploaded_by || '-',
            sortable: true
        },
        {
            name: 'Rows',
            selector: row => row.total_rows || 0,
            sortable: true,
            center: true,
            width: '90px'
        },
        {
            name: 'Imported',
            selector: row => row.imported || 0,
            cell: row => <span className="txt-success fw-bold">{row.imported || 0}</span>,
            sortable: true,
            center: true,
            width: '110px'
        },
        {
            name: 'Skipped',
            selector: row => row.skipped || 0,
            cell: row => <span className="txt-warning fw-bold">{row.skipped || 0}</span>,
            sortable: true,
            center: true,
            width: '105px'
        },
        {
            name: 'Failed',
            selector: row => row.failed || 0,
            cell: row => <span className="txt-danger fw-bold">{row.failed || 0}</span>,
            sortable: true,
            center: true,
            width: '95px'
        },
        {
            name: 'Transactions',
            selector: row => row.transactions_created || 0,
            sortable: true,
            center: true,
            width: '130px'
        }
    ];

    return (
        <Fragment>
            <Card>
                <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <H6 attrH4={{ className: "card-title mb-0" }}>Bulk Import</H6>
                        <Btn attrBtn={{ color: 'primary', onClick: handleShowHistory }}>
                            <i className="fa fa-history me-2"></i>
                            Upload History
                        </Btn>
                    </div>
                </CardHeader>
                <CardBody>
                    {loading === true ? (
                        <div className="loader-box">
                            <Spinner attrSpinner={{ className: 'loader-3' }} />
                        </div>
                    ) : (
                        <Row>
                            {/* Download Template */}
                            <Col md="12">
                                <FormGroup>
                                    <Label className="fw-bold mb-2">Download Template</Label>
                                    <p className="mb-2">
                                        Use the official template to ensure correct column mapping. Do not modify the column headers.
                                        The Instructions sheet inside the file explains every column and rule.
                                    </p>
                                    <div>
                                        <Btn attrBtn={{ color: 'primary', onClick: handleTemplateDownload }}>
                                            Download Template (.xlsx)
                                        </Btn>
                                    </div>
                                </FormGroup>
                            </Col>

                            {/* File Drop Zone */}
                            <Col md="12" className="mt-4">
                                <FormGroup>
                                    <Label className="fw-bold mb-2">Select File</Label>
                                    <div
                                        onClick={() => fileRef.current?.click()}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => { e.preventDefault(); handleFileChange(e.dataTransfer.files[0]); }}
                                        className="border rounded text-center p-4"
                                        style={{
                                            borderStyle: 'dashed',
                                            borderColor: file ? 'var(--theme-default, #7366ff)' : '#ced4da',
                                            background: file ? 'rgba(115,102,255,0.05)' : '#f8f9fa',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        {file ? (
                                            <>
                                                <p className="mb-0 fw-bold" style={{ color: 'var(--theme-default, #7366ff)' }}>{file.name}</p>
                                                <small className="text-muted">{(file.size / 1024).toFixed(1)} KB · Click to change</small>
                                            </>
                                        ) : (
                                            <>
                                                <div style={{ fontSize: 28, marginBottom: 8 }}>
                                                    📂
                                                </div>
                                                <p className="mb-0 fw-bold">Drop your file here, or click to browse</p>
                                                <small className="text-muted">Supports .xlsx and .csv</small>
                                            </>
                                        )}
                                        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
                                            onChange={(e) => handleFileChange(e.target.files[0])} />
                                    </div>
                                    {error && <small className="text-danger mt-1 d-block">{error}</small>}
                                </FormGroup>
                            </Col>

                            {/* Action Buttons */}
                            <Col md="12">
                                <Btn attrBtn={{ color: 'primary', className: 'me-3', onClick: handleUpload }}>
                                    Upload File
                                </Btn>
                                {file && (
                                    <Btn attrBtn={{ color: 'primary', className: 'me-3', onClick: handleReset }}>Reset</Btn>
                                )}
                            </Col>
                        </Row>
                    )}
                </CardBody>
            </Card>

            {uploadResult && (
                <Modal
                    className="modal-lg modal-dialog-centered"
                    isOpen={resultModal}
                    toggle={() => setResultModal(false)}
                    backdrop="static"
                >
                    <ModalHeader toggle={() => setResultModal(false)}>
                        <i className="fa fa-upload me-2"></i>
                        Upload Summary
                    </ModalHeader>

                    <ModalBody>
                        <Row className="mb-2">
                            <Col xl="3" sm="6">
                                <Widgets1
                                    data={{
                                        title: 'Total Rows',
                                        total: uploadResult.upload_summary?.total_rows_uploaded || 0,
                                        color: 'primary',
                                        icon: 'customers'
                                    }}
                                />
                            </Col>
                            <Col xl="3" sm="6">
                                <Widgets1
                                    data={{
                                        title: 'Imported',
                                        total: uploadResult.upload_summary?.imported || 0,
                                        color: 'success',
                                        icon: 'customers'
                                    }}
                                />
                            </Col>
                            <Col xl="3" sm="6">
                                <Widgets1
                                    data={{
                                        title: 'Skipped',
                                        total: uploadResult.upload_summary?.skipped || 0,
                                        color: 'warning',
                                        icon: 'customers'
                                    }}
                                />
                            </Col>
                            <Col xl="3" sm="6">
                                <Widgets1
                                    data={{
                                        title: 'Failed',
                                        total: uploadResult.upload_summary?.failed || 0,
                                        color: 'secondary',
                                        icon: 'customers'
                                    }}
                                />
                            </Col>
                        </Row>

                        <H5 attrH5={{ className: 'mb-3' }}>
                            {uploadResult.upload_summary?.transactions_created || 0} sales transactions created
                        </H5>

                        {buildErrorRows(uploadResult).length > 0 ? (
                            <>
                                <Label className="fw-bold mb-3">Rows Not Imported</Label>
                                <DataTable
                                    data={buildErrorRows(uploadResult)}
                                    columns={errorColumns}
                                    striped
                                    pagination
                                    highlightOnHover
                                    dense
                                />
                            </>
                        ) : (
                            <p className="mb-0 txt-success">
                                {allImportedMessage(uploadResult.upload_summary?.imported || 0)}
                            </p>
                        )}
                    </ModalBody>

                    <ModalFooter>
                        <Btn attrBtn={{ color: 'primary', onClick: () => setResultModal(false) }}>
                            OK
                        </Btn>
                    </ModalFooter>
                </Modal>
            )}

            <Modal
                className="modal-xl modal-dialog-centered"
                isOpen={historyModal}
                toggle={() => setHistoryModal(false)}
            >
                <ModalHeader toggle={() => setHistoryModal(false)}>
                    <i className="fa fa-history me-2"></i>
                    Bulk Upload History
                </ModalHeader>

                <ModalBody>
                    {historyLoading === true ? (
                        <div className="loader-box">
                            <Spinner attrSpinner={{ className: 'loader-3' }} />
                        </div>
                    ) : history.length === 0 ? (
                        <p className="mb-0">No bulk uploads have been performed yet.</p>
                    ) : (
                        <>
                            <p className="text-muted">
                                Showing the {history.length} most recent upload{history.length === 1 ? '' : 's'}.
                                Click a row to see the rows that were not imported.
                            </p>
                            <DataTable
                                data={history}
                                columns={historyColumns}
                                striped
                                pagination
                                highlightOnHover
                                dense
                                expandableRows
                                expandOnRowClicked
                                expandableRowsComponent={HistoryDetail}
                            />
                        </>
                    )}
                </ModalBody>

                <ModalFooter>
                    <Btn attrBtn={{ color: 'primary', onClick: () => setHistoryModal(false) }}>
                        Close
                    </Btn>
                </ModalFooter>
            </Modal>
        </Fragment>
    )
}

export default BulkImportView
